import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTarget, FiUser, FiTrendingUp, FiShield } = FiIcons;

const BrandPillars = () => {
  const pillars = [
    {
      icon: FiTarget,
      title: 'Precision',
      description: 'Every algorithm, every insight, every recommendation is crafted with surgical precision to deliver exactly what you need.'
    },
    {
      icon: FiUser,
      title: 'Personalization',
      description: 'AI that understands you as an individual, adapting to your unique patterns, preferences, and potential.'
    },
    {
      icon: FiTrendingUp,
      title: 'Performance',
      description: 'Built for scale and speed, our technology delivers exponential improvements in personal and organizational outcomes.'
    },
    {
      icon: FiShield,
      title: 'Privacy',
      description: 'Your data is yours. We build trust through transparency and security that puts you in complete control.'
    }
  ];

  return (
    <section className="py-24 relative bg-gray-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center space-y-16"
        >
          {/* Section Header */}
          <div className="space-y-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold"
            >
              Our <span className="gradient-text">Principles</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              viewport={{ once: true }}
              className="text-xl text-gray-300 max-w-3xl mx-auto"
            >
              The four pillars that guide everything we build
            </motion.p>
          </div>

          {/* Pillars Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="group space-y-6 p-8 glass-effect hover:bg-gray-900/50 transition-all duration-300"
              >
                {/* Icon */}
                <motion.div
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  className="w-16 h-16 bg-gradient-to-br from-primary-orange to-primary-dark mx-auto flex items-center justify-center group-hover:shadow-lg group-hover:shadow-primary-orange/20 transition-all duration-300"
                >
                  <SafeIcon 
                    icon={pillar.icon} 
                    className="w-8 h-8 text-black" 
                  />
                </motion.div>

                {/* Content */}
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold group-hover:text-primary-orange transition-colors duration-300">
                    {pillar.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {pillar.description}
                  </p>
                </div>

                {/* Accent Line */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  transition={{ delay: (index * 0.1) + 0.5, duration: 0.8 }}
                  viewport={{ once: true }}
                  className="h-px bg-gradient-to-r from-transparent via-primary-orange to-transparent"
                ></motion.div>
              </motion.div>
            ))}
          </div>

          {/* Bottom Accent */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8, duration: 1 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <div className="flex space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <motion.div
                  key={i}
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.2
                  }}
                  className="w-3 h-3 bg-primary-orange rounded-full"
                ></motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default BrandPillars;